import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatPrice } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";

export const QuickView = ({ product, open, onOpenChange }) => {
  const { t } = useLanguage();
  const { addItem } = useCart();
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);

  useEffect(() => {
    if (open) {
      setSize(product.sizes?.[0] || null);
      setColor(product.colors?.[0] || null);
    }
  }, [open, product]);

  const onSale = !!product.sale_price;
  const soldOut = product.stock <= 0;

  const handleAdd = () => {
    addItem(product, { quantity: 1, size, color });
    toast.success(`${product.name} ${t("added to bag")}`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 gap-0 overflow-hidden rounded-none border-border" data-testid="quickview-modal">
        <DialogTitle className="sr-only">{product.name}</DialogTitle>
        <DialogDescription className="sr-only">Quick view of {product.name}</DialogDescription>
        <div className="grid md:grid-cols-2">
          <div className="bg-secondary aspect-square md:aspect-auto">
            <img src={product.images?.[0]} alt={product.name} className="h-full w-full object-cover" />
          </div>
          <div className="p-8 flex flex-col">
            <p className="overline text-muted-foreground mb-2">{product.category}</p>
            <h2 className="font-serif text-3xl leading-tight">{product.name}</h2>
            <div className="mt-4 flex items-center gap-3">
              {onSale ? (
                <>
                  <span className="text-gold text-xl font-medium">{formatPrice(product.sale_price)}</span>
                  <span className="text-muted-foreground line-through">{formatPrice(product.price)}</span>
                </>
              ) : (
                <span className="text-xl font-medium">{formatPrice(product.price)}</span>
              )}
            </div>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed line-clamp-4">{product.description}</p>

            {product.sizes?.length > 0 && (
              <div className="mt-6">
                <p className="overline mb-2">{t("Size")}</p>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((s) => (
                    <button
                      key={s}
                      onClick={() => setSize(s)}
                      className={`min-w-10 h-9 px-3 text-sm border transition-colors ${size === s ? "border-gold text-gold" : "border-border hover:border-foreground"}`}
                      data-testid={`quickview-size-${s}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {product.colors?.length > 0 && (
              <div className="mt-4">
                <p className="overline mb-2">{t("Finish")}</p>
                <div className="flex flex-wrap gap-2">
                  {product.colors.map((c) => (
                    <button
                      key={c}
                      onClick={() => setColor(c)}
                      className={`h-9 px-3 text-sm border transition-colors ${color === c ? "border-gold text-gold" : "border-border hover:border-foreground"}`}
                      data-testid={`quickview-color-${c}`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-auto pt-8 space-y-3">
              <Button className="w-full rounded-none h-12" onClick={handleAdd} disabled={soldOut} data-testid="quickview-add-to-cart">
                <ShoppingBag className="h-4 w-4 mr-2" />
                {soldOut ? t("Sold Out") : t("Add to Bag")}
              </Button>
              <Link to={`/product/${product.id}`} onClick={() => onOpenChange(false)}>
                <Button variant="ghost" className="w-full rounded-none group" data-testid="quickview-view-details">
                  {t("View Full Details")}
                  <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
